import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, TrendingUp, Users, Star, ArrowRight } from "lucide-react";

function WhatsAppMockup() {
  const msgs = [
    { from: "user", text: "Hi, I need help tracking my order #2847" },
    { from: "bot", text: "Found order #2847 — dispatched, arriving tomorrow by 3 PM." },
    { from: "user", text: "Can I change the delivery address?" },
    { from: "bot", text: "Done. Address updated. Confirmation sent to your email." },
  ];
  return (
    <div className="h-full flex flex-col p-4 gap-3 bg-[#0A0A0D]">
      <div className="flex items-center gap-2.5 pb-3 border-b border-white/[0.05]">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500/30 to-blue-500/30 flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] text-white font-bold">AF</span>
        </div>
        <div>
          <p className="text-[12px] font-semibold text-white/80">AIFlow Support Bot</p>
          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className="text-[10px] text-emerald-400/80">Online · Avg. 2s response</span></div>
        </div>
      </div>
      <div className="flex-1 flex flex-col gap-2 overflow-hidden justify-end">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] px-3 py-1.5 rounded-2xl text-[11px] leading-relaxed ${
              m.from === "user" ? "bg-blue-600/80 text-white" : "bg-white/[0.06] text-white/70 border border-white/[0.05]"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        <div className="flex justify-start">
          <div className="px-3 py-1.5 rounded-2xl bg-white/[0.04] flex gap-1">
            {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-white/25 animate-bounce" style={{animationDelay:`${i*0.15}s`}} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function CRMMockup() {
  const leads = [
    { name: "Sarah K.", status: "Hot", score: 94, value: "$4,200" },
    { name: "Marcus T.", status: "Warm", score: 72, value: "$1,800" },
    { name: "Alex R.", status: "Hot", score: 88, value: "$3,600" },
  ];
  return (
    <div className="p-4 h-full flex flex-col gap-3 bg-[#0A0A0D]">
      <div className="flex justify-between items-center">
        <p className="text-[11px] font-semibold text-white/60 tracking-tight">AI Lead Scoring</p>
        <span className="text-[9.5px] font-mono text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded">Live</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[{v:"247",l:"Leads"},{v:"$28K",l:"Pipeline"},{v:"94%",l:"Accuracy"}].map(s=>(
          <div key={s.l} className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-2 text-center">
            <p className="text-[13px] font-bold text-white">{s.v}</p>
            <p className="text-[9px] text-white/35">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="flex-1 space-y-1.5 overflow-hidden">
        {leads.map((l,i)=>(
          <div key={i} className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white/[0.03] border border-white/[0.04]">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500/30 to-violet-500/30 flex items-center justify-center text-[9px] text-white font-bold flex-shrink-0">{l.name[0]}</div>
            <span className="flex-1 text-[11px] text-white/60 truncate">{l.name}</span>
            <span className={`text-[8.5px] px-1.5 py-0.5 rounded font-semibold ${l.status==="Hot"?"bg-red-500/15 text-red-400":"bg-yellow-500/15 text-yellow-400"}`}>{l.status}</span>
            <span className="text-[10px] text-emerald-400 font-mono">{l.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupportMockup() {
  const tickets = [
    { id:"#4821", text:"Password reset issue", status:"Resolved", time:"2s" },
    { id:"#4822", text:"Billing inquiry Q4", status:"Resolved", time:"4s" },
    { id:"#4823", text:"Integration setup help", status:"In Progress", time:"—" },
  ];
  return (
    <div className="p-4 h-full flex flex-col gap-3 bg-[#0A0A0D]">
      <div className="flex justify-between items-center">
        <p className="text-[11px] font-semibold text-white/60">AI Support Center</p>
        <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400"/><span className="text-[10px] text-emerald-400/80">84% auto-resolved</span></div>
      </div>
      <div className="flex-1 space-y-2">
        {tickets.map((t,i)=>(
          <div key={i} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.04]">
            <span className="text-[9.5px] font-mono text-white/25 flex-shrink-0">{t.id}</span>
            <span className="flex-1 text-[11px] text-white/55 truncate">{t.text}</span>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {t.status==="Resolved" && <Clock className="w-3 h-3 text-blue-400/70"/>}
              <span className={`text-[9px] font-semibold ${t.status==="Resolved"?"text-emerald-400":"text-yellow-400"}`}>{t.time==="—"?t.status:t.time}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]">
        <Star className="w-3 h-3 text-yellow-400/80"/><span className="text-[11px] text-white/40">Avg resolution: <span className="text-emerald-400 font-semibold">3.2 seconds</span></span>
      </div>
    </div>
  );
}

function LeadPipelineMockup() {
  const stages = [
    { label: "Sourced", count: 1247, pct: 100, color: "bg-blue-500/70" },
    { label: "Qualified", count: 312, pct: 25, color: "bg-violet-500/70" },
    { label: "Engaged", count: 89, pct: 7, color: "bg-yellow-500/70" },
    { label: "Closed", count: 24, pct: 2, color: "bg-emerald-500/70" },
  ];
  return (
    <div className="p-4 h-full flex flex-col gap-3 bg-[#0A0A0D]">
      <div className="flex justify-between items-center">
        <p className="text-[11px] font-semibold text-white/60">Lead Gen Pipeline</p>
        <div className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-emerald-400"/><span className="text-[10px] text-emerald-400/80">+38% MoM</span></div>
      </div>
      <div className="flex-1 space-y-3">
        {stages.map((s,i)=>(
          <div key={i} className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-white/55">{s.label}</span>
              <span className="text-[10.5px] font-mono text-white/35">{s.count.toLocaleString()}</span>
            </div>
            <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${s.pct}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: i * 0.12, ease: "easeOut" }}
                className={`h-full rounded-full ${s.color}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]">
        <Users className="w-3 h-3 text-blue-400/70"/>
        <span className="text-[11px] text-white/40">AI processes <span className="text-white/60 font-medium">24 new leads/hr</span></span>
      </div>
    </div>
  );
}

const cards = [
  { title: "WhatsApp Automation", sub: "AI-powered conversations", tag: "WhatsApp", MockupComponent: WhatsAppMockup, wide: true },
  { title: "CRM Automation", sub: "AI lead scoring & enrichment", tag: "CRM", MockupComponent: CRMMockup },
  { title: "AI Customer Support", sub: "Instant ticket resolution", tag: "Support", MockupComponent: SupportMockup },
  { title: "Lead Generation", sub: "Automated outbound pipeline", tag: "Leads", MockupComponent: LeadPipelineMockup, wide: true },
];

export default function FeaturedAutomations() {
  return (
    <section id="automations" data-testid="automations-section" className="py-24 md:py-36 relative">
      <div className="section-divider" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-lg">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="overline-badge mb-4">
              Featured Automations
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-[2.2rem] sm:text-[2.8rem] lg:text-[3.2rem] font-black tracking-[-0.04em] text-white leading-[1.05]"
            >
              See it in{" "}<span className="gradient-text-static">action.</span>
            </motion.h2>
          </div>
          <a href="#cta" data-testid="automations-cta" className="inline-flex items-center gap-1.5 text-[13px] text-blue-400/80 hover:text-blue-300 transition-colors font-medium group whitespace-nowrap">
            Get yours built <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map((card, i) => {
            const Mock = card.MockupComponent;
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`rounded-2xl border border-white/[0.06] bg-[#0E0E12] overflow-hidden group hover:border-white/[0.1] transition-all duration-300 hover:-translate-y-1 card-inner-top ${card.wide ? "lg:col-span-2" : ""}`}
                style={{ boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset" }}
              >
                <div className="h-56 border-b border-white/[0.05] overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0E0E12]/20 z-10 pointer-events-none" />
                  <Mock />
                </div>
                <div className="px-5 py-4 flex items-center gap-3">
                  <span className="text-[9.5px] font-mono text-white/25 bg-white/[0.03] border border-white/[0.05] px-2 py-0.5 rounded flex-shrink-0">{card.tag}</span>
                  <div>
                    <p className="text-[13.5px] font-semibold text-white/80 tracking-[-0.01em]">{card.title}</p>
                    <p className="text-[11.5px] text-white/30">{card.sub}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
